import { useCallback, useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import { contactsApi } from '../../api/contacts'
import { outreachApi } from '../../api/outreach'
import { RefusedError } from '../../api/client'
import { Avatar } from '../../components/Avatar'
import { VoteBadge } from '../../components/VoteButtons'
import { useSpoke } from '../../hooks/useSpoke'
import { formatDate, formatMoney, formatRelative, formatScore } from '../../lib/format'
import {
  buildDeck, craftTarget, kindLabel, otherPursuits, positionLine, pursuitPage, sinceLine, stageWord, strengthBand, workbenchPath,
  type DeckCard,
} from '../../lib/contactManager'
import type { Engagement } from '../../types/deals'
import './contactManager.css'

// THE CONTACT MANAGER — "a Contact Manager screen that acts like a dating app, tile based, swipe left and right,
// with contact opportunities listed for each contact we look at, and ability to begin crafting" (owner,
// 2026-10-02). One person at a time, strongest reason first: the outreach list in its order, then everyone else
// off the contact list. Right is the rep's "important" vote (REQ-123), left is "less"; either way the next one
// is up. The tile says why now, lists the opportunities on file with them, and opens the workbench on one.
// Nothing here ranks or sends anything: the API ranked both lists and the Outbox is the only way out.

type Dir = 'left' | 'right' | 'down'
type Done = { card: DeckCard; vote: 1 | -1 | null }
type Ghost = { card: DeckCard; dir: Dir; dx: number; dy: number; key: number }
type PursuitsState = 'loading' | 'ready' | 'failed'

/** How far a card is dragged before letting go counts as a swipe. */
const SWIPE_THRESHOLD = 120
const MAX_TILT = 12
const FLY_MS = 420

const why = (e: unknown) => (e instanceof RefusedError ? e.refusal.message : e instanceof Error ? e.message : String(e))
const tiltOf = (dx: number) => Math.max(-MAX_TILT, Math.min(MAX_TILT, dx / 12))
const reducedMotion = () => typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
/** A press on a link, a button or a field is theirs, never the start of a swipe or a key for the deck. */
const onControl = (t: EventTarget | null) => t instanceof Element && !!t.closest('a, button, input, textarea, select, [contenteditable="true"]')

export function ContactManager() {
  const spoke = useSpoke()
  const qc = useQueryClient()
  const navigate = useNavigate()
  const outreach = useQuery({ queryKey: ['outreach', spoke], queryFn: () => outreachApi.list(spoke), staleTime: 10_000 })
  const contacts = useQuery({ queryKey: ['contacts', spoke], queryFn: () => contactsApi.list(spoke), staleTime: 15_000 })
  const referrals = useQuery({ queryKey: ['referral-sources', spoke], queryFn: () => contactsApi.referralSources(spoke), staleTime: 30_000 })

  const items = outreach.data?.items
  const rows = contacts.data?.contacts
  const referralIds = useMemo(() => new Set((referrals.data?.sources ?? []).map((s) => s.personId)), [referrals.data])
  const labels = useMemo(() => new Map((outreach.data?.counts ?? []).map((c) => [c.kind, c.label] as const)), [outreach.data])
  const deck = useMemo(() => buildDeck(items ?? [], rows ?? [], referralIds), [items, rows, referralIds])

  // what the rep has done this sitting, in order: the deck is the ranked list minus these, so a refetch after a
  // vote (which moves only the person voted on) never shuffles the cards still to come
  const [done, setDone] = useState<Done[]>([])
  // the card Undo put back: on top until the next action, whatever its vote now says about where it ranks
  const [pinned, setPinned] = useState<DeckCard | null>(null)
  const [drag, setDrag] = useState<{ dx: number; dy: number } | null>(null)
  const [ghost, setGhost] = useState<Ghost | null>(null)
  const [lastError, setLastError] = useState<string | null>(null)

  const remaining = useMemo(() => {
    const gone = new Set(done.map((d) => d.card.personId))
    const rest = deck.filter((c) => !gone.has(c.personId) && c.personId !== pinned?.personId)
    return pinned ? [pinned, ...rest] : rest
  }, [deck, done, pinned])
  const top = remaining[0] ?? null
  const peek = remaining.slice(1, 3)
  const topId = top?.personId ?? null

  // the pursuits on file: read for the top card only, as it comes up
  const memory = useQuery({ queryKey: ['contact', spoke, topId ?? ''], queryFn: () => contactsApi.memory(spoke, topId!), enabled: !!topId, staleTime: 60_000 })
  const onFile = memory.data?.pursuits ?? []
  const pursuits = useMemo(() => (top ? otherPursuits(top, onFile) : []), [top, onFile])
  const pursuitsState: PursuitsState = memory.isError ? 'failed' : memory.data ? 'ready' : 'loading'
  const craft = top ? craftTarget(top, onFile) : null
  const craftTo = craft?.to ?? null

  const write = useMutation({
    mutationFn: async ({ personId, vote }: { personId: string; vote: 1 | -1 | null }) => {
      if (vote === null) await contactsApi.clearVote(spoke, personId)
      else await contactsApi.vote(spoke, personId, vote)
    },
    onError: (e) => setLastError(why(e)),
    onSettled: (_r, _e, v) => {
      void qc.invalidateQueries({ queryKey: ['contacts', spoke] })
      void qc.invalidateQueries({ queryKey: ['outreach', spoke] })
      void qc.invalidateQueries({ queryKey: ['contact', spoke, v.personId] })
      void qc.invalidateQueries({ queryKey: ['contact-card', spoke, v.personId] })
    },
  })
  const { mutate } = write

  // the deck advances at once; the vote lands behind it, and a refusal is one line that does not stop the deck
  const act = useCallback((dir: Dir, from = { dx: 0, dy: 0 }) => {
    if (!top) return
    const vote: 1 | -1 | null = dir === 'right' ? 1 : dir === 'left' ? -1 : null
    setDone((d) => [...d, { card: top, vote }])
    setPinned(null)
    setDrag(null)
    setLastError(null)
    setGhost(reducedMotion() ? null : { card: top, dir, dx: from.dx, dy: from.dy, key: Date.now() })
    if (vote !== null) mutate({ personId: top.personId, vote })
  }, [top, mutate])

  const undo = useCallback(() => {
    const last = done[done.length - 1]
    if (!last) return
    setDone((d) => d.slice(0, -1))
    setPinned(last.card)
    setGhost(null)
    setLastError(null)
    if (last.vote !== null) mutate({ personId: last.card.personId, vote: null })
  }, [done, mutate])

  // the ghost is cosmetic: whatever happens to animationend, it is gone inside half a second
  useEffect(() => {
    if (!ghost) return
    const t = window.setTimeout(() => setGhost(null), FLY_MS + 120)
    return () => window.clearTimeout(t)
  }, [ghost])

  // keys: left = not now, right = important, down or space = skip, Enter = begin crafting
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.altKey || e.ctrlKey || e.metaKey) return
      const t = e.target as HTMLElement | null
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) return
      const control = onControl(t)
      if (e.key === 'ArrowLeft') { e.preventDefault(); act('left') }
      else if (e.key === 'ArrowRight') { e.preventDefault(); act('right') }
      else if (e.key === 'ArrowDown' || (e.key === ' ' && !control)) { e.preventDefault(); act('down') }
      else if (e.key === 'Enter' && !control && craftTo) { e.preventDefault(); navigate(craftTo) }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [act, craftTo, navigate])

  // the swipe: pointer events on the top card, captured so the drag survives leaving it; a press on a link or a
  // button is a click, not a drag
  const start = useRef<{ x: number; y: number; id: number } | null>(null)
  const onPointerDown = (e: ReactPointerEvent<HTMLElement>) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return
    if (onControl(e.target)) return
    start.current = { x: e.clientX, y: e.clientY, id: e.pointerId }
    e.currentTarget.setPointerCapture(e.pointerId)
    setDrag({ dx: 0, dy: 0 })
  }
  const onPointerMove = (e: ReactPointerEvent<HTMLElement>) => {
    const s = start.current
    if (!s || s.id !== e.pointerId) return
    setDrag({ dx: e.clientX - s.x, dy: e.clientY - s.y })
  }
  const onPointerUp = (e: ReactPointerEvent<HTMLElement>) => {
    const s = start.current
    if (!s || s.id !== e.pointerId) return
    start.current = null
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId)
    const dx = e.clientX - s.x
    const dy = e.clientY - s.y
    if (Math.abs(dx) >= SWIPE_THRESHOLD) act(dx > 0 ? 'right' : 'left', { dx, dy })
    else setDrag(null)
  }
  const onPointerCancel = () => { start.current = null; setDrag(null) }

  if (outreach.isPending || contacts.isPending) return <p className="muted centered">Loading…</p>
  if (!items && !rows) {
    return (
      <section className="page contact-manager">
        <p className="kicker">Contacts</p>
        <h1>Who's next</h1>
        <div className="refusal">
          <strong>Nothing to read yet</strong>
          <p>{why(contacts.error ?? outreach.error)}</p>
        </div>
      </section>
    )
  }

  const partial = outreach.isError ? `The outreach list could not be read, so there are no reasons on the tiles today: ${why(outreach.error)}`
    : contacts.isError ? `The contact list could not be read, so only the outreach list is on the deck: ${why(contacts.error)}` : null
  const dx = drag?.dx ?? 0
  const dy = drag?.dy ?? 0
  const pull = Math.min(1, Math.abs(dx) / SWIPE_THRESHOLD)
  const topStyle = drag ? { transform: `translate(${Math.round(dx)}px, ${Math.round(dy * 0.25)}px) rotate(${tiltOf(dx).toFixed(1)}deg)`, transition: 'none' } : undefined
  const last = done[done.length - 1]

  return (
    <section className="page contact-manager">
      <header className="deals-head">
        <div>
          <p className="kicker">Contacts</p>
          <h1>Who's next</h1>
          <p className="lede">One person at a time, strongest reason first. Right if you want them near the top, left if not now; either way the next one is up.</p>
          <p className="cm-position muted small">{positionLine(done.length, deck.length)}</p>
        </div>
        <div className="actions">
          <Link className="button" to="/contacts">Everyone, as a list</Link>
          <Link className="button" to="/outreach">Outreach</Link>
        </div>
      </header>

      {partial && <p className="error small" role="note">{partial}</p>}
      {lastError && <p className="error small" role="alert">The vote did not land: {lastError}</p>}

      <div className="cm-stage">
        <div className={`cm-stack ${drag ? 'dragging' : ''}`}>
          {peek.slice().reverse().map((c, i) => <PeekCard key={c.personId} card={c} depth={peek.length - i} />)}

          {top && craft ? (
            <article
              className={`cm-card top ${drag ? 'dragging' : ''}`}
              style={topStyle}
              aria-label={`${top.name}, ${positionLine(done.length, deck.length)}`}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerCancel={onPointerCancel}
            >
              <span className="cm-stamp left" style={{ opacity: dx < 0 ? pull : 0 }} aria-hidden="true">Not now</span>
              <span className="cm-stamp right" style={{ opacity: dx > 0 ? pull : 0 }} aria-hidden="true">Important</span>
              <CardFace card={top} labels={labels} pursuits={pursuits} pursuitsState={pursuitsState} />
              <footer className="cm-foot">
                <div className="cm-primary">
                  <button type="button" className="button not-now" onClick={() => act('left')} title="Less: still a prospect, shown below everyone else on every ranked list. Left arrow, or swipe left.">Not now</button>
                  <Link className="button primary" to={craft.to} title={craft.label === 'Open card' ? 'No account is on file for them, so there is no workbench to open; the card has everything on file.' : 'The workbench on their account: a conversation that moves the draft, and a message that leaves for your Outbox. Enter.'}>{craft.label}</Link>
                  <button type="button" className="button important" onClick={() => act('right')} title="Important: at the top of every list where contacts are ranked. Right arrow, or swipe right.">Important</button>
                </div>
                <div className="cm-secondary">
                  <button type="button" className="link-button small" onClick={() => act('down')} title="The next one, with no vote either way. Down arrow or space.">Skip</button>
                  <Link className="small" to={`/contacts/${encodeURIComponent(top.personId)}`}>Open card</Link>
                </div>
              </footer>
            </article>
          ) : (
            <EndCard total={deck.length} />
          )}

          {ghost && (
            <div
              key={ghost.key}
              className={`cm-card cm-ghost fly-${ghost.dir}`}
              aria-hidden="true"
              style={{ ['--dx' as string]: `${Math.round(ghost.dx)}px`, ['--dy' as string]: `${Math.round(ghost.dy * 0.25)}px`, ['--rot' as string]: `${tiltOf(ghost.dx).toFixed(1)}deg` }}
              onAnimationEnd={() => setGhost(null)}
            >
              <CardFace card={ghost.card} labels={labels} pursuits={[]} pursuitsState="ready" compact />
            </div>
          )}
        </div>

        <p className="cm-under">
          <button type="button" className="link-button" onClick={undo} disabled={!last}>Undo</button>
          {last && <span className="muted small">puts {last.card.name} back{last.vote ? ' and takes the vote back' : ''}</span>}
        </p>
        <p className="muted small cm-keys">Left arrow is not now, right is important, down skips, Enter begins crafting. Drag the tile either way on a touch screen.</p>
      </div>
    </section>
  )
}

/* ── the tile ── */

function CardFace({ card, labels, pursuits, pursuitsState, compact = false }: {
  card: DeckCard
  labels: ReadonlyMap<string, string>
  pursuits: Engagement[]
  pursuitsState: PursuitsState
  /** the flying copy: no links, no pursuit list */
  compact?: boolean
}) {
  const pct = card.strength == null ? null : Number(formatScore(card.strength * 100))
  return (
    <>
      <div className="cm-head">
        <Avatar name={card.name} photoUrl={card.photoUrl} persona={card.persona} role={card.role} size={72} />
        <div className="cm-who">
          <h2 className="cm-name">
            {compact ? <span>{card.name}</span> : <Link to={`/contacts/${encodeURIComponent(card.personId)}`}>{card.name}</Link>}
            <VoteBadge vote={card.vote} />
          </h2>
          <p className="cm-line">
            {card.title && <span>{card.title}</span>}
            {card.company && (
              <>
                {card.title && <span className="cm-dot" aria-hidden="true"> · </span>}
                {card.companyId && !compact ? <Link to={`/accounts/${encodeURIComponent(card.companyId)}`}>{card.company}</Link> : <span>{card.company}</span>}
              </>
            )}
            {card.location && <><span className="cm-dot" aria-hidden="true"> · </span><span className="muted">{card.location}</span></>}
            {!card.title && !card.company && !card.location && <span className="muted">Nothing on file about where they work</span>}
          </p>
          {((card.role && card.role !== 'other') || (card.persona && card.persona !== 'other')) && (
            <p className="cm-tags">
              {card.role && card.role !== 'other' && <span className={`tag role ${card.role}`} title="What they are to the firm">{card.role}</span>}
              {card.persona && card.persona !== 'other' && <span className={`tag persona ${card.persona}`} title="The part they play inside their organisation">{card.persona}</span>}
            </p>
          )}
        </div>
        <div className="cm-strength">
          {pct !== null ? (
            <>
              <span className="strength-bar" title="How often and how recently you wrote, read from your own mail"><span className={`strength-fill ${strengthBand(card.strength)}`} style={{ width: `${Math.max(4, pct)}%` }} /></span>
              <span className="muted small">{pct}% strength</span>
            </>
          ) : <span className="muted small">Not weighed yet</span>}
          <span className="muted small" title={card.lastTouch ? formatDate(card.lastTouch) : undefined}>{card.lastTouch ? `Last touch ${formatRelative(card.lastTouch)}` : 'No touch on file'}</span>
        </div>
      </div>
      <WhyNow card={card} labels={labels} />
      {!compact && <Opportunities card={card} pursuits={pursuits} state={pursuitsState} />}
    </>
  )
}

/** Why now: the outreach list's sentence with its kind, the days, the next step; or the honest word that there is none. */
function WhyNow({ card, labels }: { card: DeckCard; labels: ReadonlyMap<string, string> }) {
  if (card.source !== 'outreach' || !card.reason) {
    return (
      <section className="cm-why none">
        <p className="kicker">Why now</p>
        <p className="cm-reason muted">No reason is on the list for them yet.</p>
        {(card.signals.dormant || card.signals.moved) && (
          <p className="cm-signals">
            {card.signals.dormant && <span className="tag sig watch" title="A tie that was warm and has gone quiet">gone quiet</span>}
            {card.signals.moved && <span className="tag sig watch" title="Their mail suggests a change of job">may have moved</span>}
          </p>
        )}
      </section>
    )
  }
  const ns = card.nextStep
  return (
    <section className={`cm-why ${card.reasonKind ?? ''}`}>
      <p className="kicker">Why now</p>
      <p className="cm-reason">{card.reason}</p>
      <p className="cm-why-meta">
        <span className={`tag kind ${card.reasonKind ?? ''}`}>{kindLabel(card.reasonKind, labels)}</span>
        <span className="muted small">{sinceLine(card.daysSinceContact)}</span>
      </p>
      {ns && (
        <p className={`cm-next ${ns.overdue ? 'overdue' : ''}`}>
          <strong>Next step.</strong> {ns.text}
          <span className="muted small"> — {ns.owner}{ns.by ? `, by ${formatDate(ns.by)}` : ''}</span>
          {ns.overdue && <span className="error small"> · overdue {ns.daysOverdue} {ns.daysOverdue === 1 ? 'day' : 'days'}</span>}
        </p>
      )}
    </section>
  )
}

/** The opportunities with this contact: the one the list put on the tile, then everything on file, each a way into the workbench. */
function Opportunities({ card, pursuits, state }: { card: DeckCard; pursuits: Engagement[]; state: PursuitsState }) {
  const open = card.openPursuit
  const any = !!open || pursuits.length > 0
  return (
    <section className="cm-opps">
      <p className="kicker">Opportunities with this contact</p>
      {any && (
        <ul className="plain cm-opp-list">
          {open && (
            <li className="cm-opp">
              <div className="cm-opp-words">
                <span className={`tag stage ${open.stage}`}>{stageWord(open.stage)}</span>
                <strong>{open.subject}</strong>
                {open.amount != null && <span className="muted small">{formatMoney(open.amount)}</span>}
                <span className="muted small">on the list today</span>
              </div>
              {card.companyId
                ? <Link className="button small" to={workbenchPath(card.companyId, card.personId, open.engagementId)}>Craft on this</Link>
                : <Link className="button small" to={pursuitPage(open.stage, open.engagementId)}>Open</Link>}
            </li>
          )}
          {pursuits.map((e) => (
            <li key={e.engagementId} className="cm-opp">
              <div className="cm-opp-words">
                <span className={`tag stage ${e.stage}`}>{stageWord(e.stage)}</span>
                <strong>{e.subject}</strong>
                {e.amount != null && <span className="muted small">{formatMoney(e.amount)}</span>}
                {e.lastActivityOn && <span className="muted small">last activity {formatDate(e.lastActivityOn)}</span>}
              </div>
              <Link className="button small" to={workbenchPath(e.companyId || card.companyId || '', card.personId, e.engagementId)}>Craft on this</Link>
            </li>
          ))}
        </ul>
      )}
      {state === 'loading' && <p className="muted small cm-opp-note">Reading what is on file…</p>}
      {state === 'failed' && <p className="muted small cm-opp-note">The pursuits on file could not be read.</p>}
      {state === 'ready' && !any && (
        <p className="cm-opp-none">
          <span className="muted small">No opportunity on file with them yet.</span>
          {card.companyId
            ? <Link className="button small" to={workbenchPath(card.companyId, card.personId)} title="The workbench lets you set one up before a word is drafted.">Start one</Link>
            : <Link className="button small" to={`/contacts/${encodeURIComponent(card.personId)}`} title="No account is on file for them yet; the card is where that is put right.">Open card</Link>}
        </p>
      )}
    </section>
  )
}

/** The next one or two, peeking from behind: the head only, so the stack reads as a stack and nothing in it can be pressed. */
function PeekCard({ card, depth }: { card: DeckCard; depth: number }) {
  return (
    <div className={`cm-card cm-peek depth-${depth}`} aria-hidden="true">
      <div className="cm-head">
        <Avatar name={card.name} photoUrl={card.photoUrl} persona={card.persona} role={card.role} size={72} />
        <div className="cm-who">
          <h2 className="cm-name"><span>{card.name}</span></h2>
          <p className="cm-line muted">{[card.title, card.company].filter(Boolean).join(' · ')}</p>
        </div>
      </div>
    </div>
  )
}

/** The deck is done, or was never dealt: say which, and where to go from here. */
function EndCard({ total }: { total: number }) {
  return (
    <div className="cm-card cm-end">
      <strong>{total === 0 ? 'Nobody is on the deck yet' : 'That is everyone'}</strong>
      <p className="muted">
        {total === 0
          ? 'The deck is dealt from the people you write to. Connect your mailbox and read it, and they come up here one at a time.'
          : `You have been through all ${total}. Important goes to the top of every ranked list; not now sits below everyone else. The list has them all.`}
      </p>
      <div className="actions">
        <Link className="button" to="/contacts/list">Everyone, as a list</Link>
        <Link className="button" to="/outreach">Outreach</Link>
      </div>
    </div>
  )
}
